import { useId, useRef, useState } from 'react';
import { MAX_UPLOAD_BYTES } from '../api/client';

interface FilePickerProps {
  file: File | null;
  onFile: (file: File | null) => void;
  disabled?: boolean;
  label?: string;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

export function FilePicker({ file, onFile, disabled, label = 'PowerPoint file (.pptx)' }: FilePickerProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const accept = (candidate: File | undefined | null) => {
    if (!candidate) return;
    if (!candidate.name.toLowerCase().endsWith('.pptx')) {
      setProblem('Choose a PowerPoint file ending in .pptx.');
      onFile(null);
      return;
    }
    if (candidate.size > MAX_UPLOAD_BYTES) {
      setProblem('The file is larger than 100 MB.');
      onFile(null);
      return;
    }
    setProblem(null);
    onFile(candidate);
  };

  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div
        className={`dropzone${active ? ' dropzone-active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setActive(false);
          if (disabled) return;
          accept(event.dataTransfer.files[0]);
        }}
      >
        {file ? (
          <strong>
            {file.name} <span className="muted">({formatSize(file.size)})</span>
          </strong>
        ) : (
          <span>Drag a .pptx file here, or</span>
        )}
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          className="visually-hidden"
          tabIndex={-1}
          disabled={disabled}
          onChange={(event) => {
            accept(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => inputRef.current?.click()}>
          {file ? 'Choose another file' : 'Choose a file'}
        </button>
        <span className="xsmall">Up to 100 MB.</span>
      </div>
      {problem ? (
        <div className="alert alert-error" role="alert">
          {problem}
        </div>
      ) : null}
    </div>
  );
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div
        className="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={label ?? 'Upload progress'}
      >
        <div className="progress-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="xsmall muted">
        {pct < 100 ? `Uploading… ${pct}%` : 'Uploaded. Processing on the server…'}
      </span>
    </div>
  );
}
